import {useRef, useState} from "react"
import {ref, getDownloadURL} from "firebase/storage"
import {storage} from "@/lib/firebase"
import {uploadFile} from "@/lib/uploadFile"
import {toStaticImage} from "@/lib/toStaticImage"
import {useAppDispatch, useAppSelector} from "@/store/hooks"
import {updateUserAvatarSaga} from "@/store/auth/authSlice"
import {selectAuth} from "@/store/auth/authSelectors"
import Avatar from "@/components/ui/Avatar/Avatar"
import DropdownWrapper from "@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper"
import Dropdown from "@/components/ui/Dropdown/Dropdown"
import DropdownItem from "@/components/ui/Dropdown/components/DropdownItem/DropdownItem"
import {TextNormBold, TextMidd} from "@/components/ui/Text"
import css from "./AccountSettingsAvatar.module.css"

const AccountSettingsAvatar = () => {
    const dispatch = useAppDispatch()
    const auth = useAppSelector(selectAuth)
    const inputRef = useRef<HTMLInputElement>(null)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    if (!auth) return null

    const handleClick = () => {
        if (auth.avatarImage) {
            setIsDropdownOpen(true)
        } else {
            inputRef.current?.click()
        }
    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const staticFile = await toStaticImage(file)
        const storageRef = ref(storage, `avatars/${auth.id}/${staticFile.name}`)
        await uploadFile(storageRef, staticFile)
        const url = await getDownloadURL(storageRef)
        dispatch(updateUserAvatarSaga(url))
        e.target.value = ''
    }

    const handleReplace = () => {
        setIsDropdownOpen(false)
        inputRef.current?.click()
    }

    const handleDelete = () => {
        setIsDropdownOpen(false)
        dispatch(updateUserAvatarSaga(null))
    }

    return (
        <div className={css.container}>
            <DropdownWrapper>
                <div className={css.avatar} onClick={handleClick}>
                    <Avatar
                        title={auth.fullName || auth.username}
                        color={auth.color}
                        avatar={auth.avatarImage || null}
                        size="big"
                    />
                </div>
                <Dropdown isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
                    <DropdownItem title="Replace" onClick={handleReplace}/>
                    <DropdownItem title="Delete" onClick={handleDelete} danger/>
                </Dropdown>
            </DropdownWrapper>
            <div className={css.info}>
                <TextNormBold>Avatar</TextNormBold>
                <TextMidd className={css.hint}>Recommended size 256×256px</TextMidd>
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className={css.input}
                onChange={handleFileChange}
            />
        </div>
    )
}

export default AccountSettingsAvatar
